import { AsyncStore } from "src/stores";
import { Logger } from "src/helpers";

/**
 * Decorates a method as an asyncOperation to handle loading and error states
 * in the UI.
 * @param requestID - an identifier for the request, used in assigning loading
 * and error states.
 */
export const asyncRequest = <
  T extends AsyncStore<any>,
  FuncName extends string
>(
  requestID: FuncName
) => {
  return (_target: T, _key: FuncName, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;
    // Must be written in function declaration format to correctly capture the context.
    descriptor.value = async function (...args: any[]) {
      const asyncStore = this as T;
      asyncStore.errorState.removeState(requestID);
      asyncStore.loadingState.addState(requestID);
      try {
        return await originalMethod.apply(asyncStore, args);
      } catch (error) {
        const tags = [_target.constructor?.name, requestID];
        if (error?.response?.status === 401) {
          // track 401 errors for RayGun filtering
          tags.push("unauthorized_request");
        }
        asyncStore.errorState.addState(requestID);
        Logger.error(error, tags);
        throw error;
      } finally {
        asyncStore.loadingState.removeState(requestID);
      }
    };
  };
};
